<template>
    <div
        :class="[
            'absolute',
            'text-theme-text',
            'p-1',
            'bg-theme-bg',
            'rounded-md',
            'border',
            'border-theme-border',
            positioningSubMenu,
            horizontalAlignmentClass,
        ]"
        :style="horizontalAlignmentStyle"
    >
        <slot />
    </div>
</template>
<script>
import { resolveDropdownPlacement } from '../../../js/utils/resolveDropdownPlacement.js';

export default {
    props: {
        submenuDisplayInDown: {
            type: Boolean,
            required: false,
            default: true,
        },
    },
    data() {
        return {
            // Measured horizontal placement: 'start' (default left anchor), 'end' (flipped to open
            // leftwards) or 'center' (centered on the viewport when it fits on neither side).
            placementMode: 'start',
            // CSS `left` (relative to the trigger) used only when placementMode is 'center'.
            centeredLeftPx: 0,
        };
    },
    computed: {
        positioningSubMenu() {
            return this.submenuDisplayInDown ? 'top-12' : 'bottom-14';
        },
        /**
         * Empty by default so dropdowns that fit keep the default left anchor and callers that set
         * their own horizontal class are left untouched; only overrides to `right-0` once the
         * default placement is measured to overflow the screen.
         * @returns {string}
         */
        horizontalAlignmentClass() {
            return this.placementMode === 'end' ? 'right-0' : '';
        },
        /**
         * When the dropdown fits on neither side it is centered on the viewport via an inline `left`
         * (which overrides any caller-provided horizontal class) and `right: auto` (which neutralizes
         * callers that anchor with a `right` class). Empty otherwise so class-based anchoring wins.
         * @returns {Object}
         */
        horizontalAlignmentStyle() {
            return this.placementMode === 'center' ? { left: `${this.centeredLeftPx}px`, right: 'auto' } : {};
        },
    },
    mounted() {
        // Parents toggle this dropdown with v-show, so lifecycle hooks don't fire when it opens.
        // Observe visibility instead and re-check the horizontal fit each time it appears.
        if (typeof IntersectionObserver !== 'undefined') {
            this.visibilityObserver = new IntersectionObserver(entries => {
                if (entries.some(entry => entry.isIntersecting)) {
                    this.$nextTick(this.adjustHorizontalAlignment);
                }
            });
            this.visibilityObserver.observe(this.$el);
        }
        // Content that changes width while the dropdown is open moves where it overflows,
        // so the fit is re-checked on every resize as well as on every appearance.
        if (typeof ResizeObserver !== 'undefined') {
            this.contentResizeObserver = new ResizeObserver(() => this.adjustHorizontalAlignment());
            this.contentResizeObserver.observe(this.$el);
        }
    },
    beforeDestroy() {
        this.visibilityObserver?.disconnect();
        this.contentResizeObserver?.disconnect();
    },
    methods: {
        /**
         * Reposition the dropdown so it never gets clipped by the viewport edges: keep the default
         * left anchor when it fits, flip to a right anchor when only that side has room, and center
         * it on the viewport when it fits on neither side. Computed from the trigger's geometry — not
         * the current placement — so it is stable and never flip-flops.
         * @returns {void}
         */
        adjustHorizontalAlignment() {
            const el = this.$el;
            const trigger = el?.offsetParent;
            if (!trigger) return;

            const triggerRect = trigger.getBoundingClientRect();
            const placement = resolveDropdownPlacement({
                triggerLeft: triggerRect.left,
                triggerRight: triggerRect.right,
                width: el.offsetWidth,
                viewportWidth: window.innerWidth,
            });

            this.placementMode = placement.mode;
            if (placement.mode === 'center') {
                this.centeredLeftPx = placement.left;
            }
        },
    },
};
</script>
